import { useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { useKudozPending } from '@/hooks/useKudozPending';
import { useUserProfile } from '@/hooks/useUserProfile';
import { KudozIcon } from '@/components/KudozIcon';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Bell, Trophy } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { UserBadge } from './types';

const BADGE_TIERS = [
  { name: 'Newcomer', minKudoz: 0 },
  { name: 'Regular', minKudoz: 100 },
  { name: 'Foodie', minKudoz: 500 },
  { name: 'Connoisseur', minKudoz: 1000 },
  { name: 'Expert', minKudoz: 2500 },
  { name: 'Legend', minKudoz: 5000 },
];

function getProgressToNextTier(balance: number): { current: string; next: string | null; progress: number; remaining: number } {
  let tierIdx = 0;
  for (let i = BADGE_TIERS.length - 1; i >= 0; i--) {
    if (balance >= BADGE_TIERS[i].minKudoz) {
      tierIdx = i;
      break;
    }
  }
  const current = BADGE_TIERS[tierIdx];
  const next = BADGE_TIERS[tierIdx + 1] ?? null;
  if (!next) {
    return { current: current.name, next: null, progress: 100, remaining: 0 };
  }
  const rangeStart = current.minKudoz;
  const rangeEnd = next.minKudoz;
  const progress = Math.min(100, Math.round(((balance - rangeStart) / (rangeEnd - rangeStart)) * 100));
  const remaining = rangeEnd - balance;
  return { current: current.name, next: next.name, progress, remaining };
}

export default function KudozWidget() {
  const [, navigate] = useLocation();
  const { pendingCount, isLoading } = useKudozPending();
  const { kudozBalance } = useUserProfile();

  const { data: userBadgesData, isLoading: badgesLoading } = useQuery<UserBadge[]>({
    queryKey: ['/api/user/badges'],
    staleTime: 2 * 60_000,
  });

  const earnedBadges = userBadgesData?.filter((b) => b.earnedAt) ?? [];
  const tierInfo = getProgressToNextTier(kudozBalance);

  if (isLoading || badgesLoading) {
    return (
      <div className="px-4 space-y-3">
        <div className="flex gap-3">
          <Skeleton className="h-16 flex-1 rounded-lg" />
          <Skeleton className="h-16 flex-1 rounded-lg" />
        </div>
        <Skeleton className="h-8 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="px-4 space-y-3">
      <div className="flex gap-3">
        <Card
          className="flex-1 hover-elevate active-elevate-2 cursor-pointer"
          onClick={() => navigate('/rewards')}
        >
          <CardContent className="p-3 flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <KudozIcon className="w-5 h-5 text-primary" />
            </div>
            <div>
              <div className="text-lg font-bold leading-tight">{kudozBalance.toLocaleString()}</div>
              <div className="text-xs text-muted-foreground">Balance</div>
            </div>
          </CardContent>
        </Card>
        <Card
          className={cn("flex-1 hover-elevate active-elevate-2 cursor-pointer", pendingCount > 0 && "border-destructive/40")}
          onClick={() => navigate('/rewards')}
        >
          <CardContent className="p-3 flex items-center gap-2">
            <div className={cn("w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0", pendingCount > 0 ? "bg-destructive/10" : "bg-muted")}>
              <Bell className={cn("w-5 h-5", pendingCount > 0 ? "text-destructive" : "text-muted-foreground")} />
            </div>
            <div>
              <div className="text-lg font-bold leading-tight">{pendingCount}</div>
              <div className="text-xs text-muted-foreground">Pending</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Progress to next badge tier */}
      <Card>
        <CardContent className="p-3 space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="font-medium">{tierInfo.current}</span>
            {tierInfo.next ? (
              <span className="text-muted-foreground">{tierInfo.remaining.toLocaleString()} to {tierInfo.next}</span>
            ) : (
              <span className="text-primary font-medium">Max tier</span>
            )}
          </div>
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all duration-500"
              style={{ width: `${tierInfo.progress}%` }}
            />
          </div>
        </CardContent>
      </Card>

      {earnedBadges.length > 0 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
          <span className="text-xs text-muted-foreground flex-shrink-0">Badges:</span>
          {earnedBadges.slice(0, 6).map((b) => (
            <Badge key={b.id} variant="secondary" className="flex-shrink-0 text-xs gap-1">
              <Trophy className="w-3 h-3" />
              {b.name}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
